import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CommercialStats {
  activeBidVolume: number;
  recurringRevenue: number;
  winRate: number;
  productionBacklogSquares: number;
}

export const useCommercialDashboardStats = (orgId?: string) => {
  const [stats, setStats] = useState<CommercialStats>({
    activeBidVolume: 0,
    recurringRevenue: 0,
    winRate: 0,
    productionBacklogSquares: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Get all commercial jobs
        const { data: commercialJobs } = await supabase
          .from('jobs')
          .select('id, status, estimated_value, property_type')
          .eq('org_id', orgId)
          .eq('property_type', 'Commercial');

        if (!commercialJobs || commercialJobs.length === 0) {
          setLoading(false);
          return;
        }

        // Active bid volume (proposed + in_progress jobs)
        const activeBidVolume = commercialJobs
          .filter(j => ['proposed', 'in_progress', 'estimating'].includes(j.status))
          .reduce((sum, j) => sum + (j.estimated_value || 0), 0);

        // Estimate recurring revenue from active commercial jobs
        // Assuming ~10% of completed jobs become recurring maintenance
        const completedJobs = commercialJobs.filter(j => j.status === 'completed');
        const avgJobValue = completedJobs.length > 0
          ? completedJobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0) / completedJobs.length
          : 0;
        const recurringRevenue = avgJobValue * 0.1; // Monthly recurring estimate

        // Calculate win rate (completed vs total)
        const totalBids = commercialJobs.length;
        const wonBids = commercialJobs.filter(j => 
          ['completed', 'in_progress'].includes(j.status)
        ).length;
        const winRate = totalBids > 0 ? (wonBids / totalBids) * 100 : 0;

        // Get production backlog from commercial estimates
        const activeJobIds = commercialJobs
          .filter(j => ['in_progress', 'scheduled'].includes(j.status))
          .map(j => j.id);

        const { data: commercialEstimates } = await supabase
          .from('commercial_estimates')
          .select('total_square_feet')
          .in('job_id', activeJobIds);

        const productionBacklogSquares = commercialEstimates?.reduce(
          (sum, est) => sum + (est.total_square_feet || 0), 
          0
        ) || 0;

        setStats({
          activeBidVolume: Math.round(activeBidVolume),
          recurringRevenue: Math.round(recurringRevenue),
          winRate: Math.round(winRate),
          productionBacklogSquares: Math.round(productionBacklogSquares),
        });
      } catch (error) {
        console.error('Error calculating commercial stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [orgId]);

  return { stats, loading };
};
